import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { WifiOff } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const GoldMark = ({ pulse = false }) => (
  <motion.div
    className="flex h-16 w-16 items-center justify-center rounded-full border border-[#D4AF37]/50"
    animate={pulse ? { scale: [1, 1.06, 1], opacity: [1, 0.75, 1] } : {}}
    transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
  >
    <span className="font-display text-3xl italic text-[#B38B2D]">R</span>
  </motion.div>
);

// Branded connection states: elegant loader on first paint, a graceful retry
// card if the clinic can't be reached, and quiet toasts on offline/online.
export const ConnectionGuard = () => {
  const { content, error, refresh } = useContent();
  const [offline, setOffline] = useState(typeof navigator !== "undefined" ? !navigator.onLine : false);
  const [backOnline, setBackOnline] = useState(false);

  useEffect(() => {
    const onOff = () => {
      setOffline(true);
      setBackOnline(false);
    };
    const onOn = () => {
      setOffline(false);
      setBackOnline(true);
      refresh();
      setTimeout(() => setBackOnline(false), 2600);
    };
    window.addEventListener("offline", onOff);
    window.addEventListener("online", onOn);
    return () => {
      window.removeEventListener("offline", onOff);
      window.removeEventListener("online", onOn);
    };
  }, [refresh]);

  if (!content && error)
    return (
      <div className="fixed inset-0 z-[95] flex flex-col items-center justify-center bg-[#FAF8F5] px-6 text-center" data-testid="content-error">
        <GoldMark />
        <h1 className="mt-8 font-display text-3xl font-normal text-[#1A1A1A] sm:text-4xl">A moment of stillness</h1>
        <p className="mt-4 max-w-sm text-sm font-light leading-relaxed text-[#5A504B]">
          We couldn't reach the clinic just now. Check your connection — we'll have everything glowing again in a breath.
        </p>
        <button onClick={refresh} data-testid="content-retry-button" className="btn-gold mt-8 px-8 py-3.5 text-sm">
          Try Again
        </button>
      </div>
    );

  if (!content)
    return (
      <div className="fixed inset-0 z-[95] flex flex-col items-center justify-center bg-[#FAF8F5]" data-testid="brand-loader">
        <GoldMark pulse />
        <p className="mt-7 text-[10px] font-medium uppercase tracking-[0.5em] text-[#8A7364]">Royal Skin</p>
        <p className="mt-2 text-[9px] uppercase tracking-[0.35em] text-[#B7A698]">Preparing your experience</p>
      </div>
    );

  return (
    <AnimatePresence>
      {(offline || backOnline) && (
        <motion.div
          key={offline ? "off" : "on"}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-24 left-1/2 z-[95] -translate-x-1/2"
          data-testid={offline ? "offline-toast" : "online-toast"}
          role="status"
        >
          <div className="flex items-center gap-2.5 rounded-full border border-[#D4AF37]/40 bg-[#1A1615]/90 px-5 py-2.5 shadow-[0_12px_40px_rgba(26,22,21,0.35)] backdrop-blur-md">
            {offline && <WifiOff className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" />}
            <span className="text-xs font-light tracking-wide text-[#F5EFE6]">
              {offline ? "You're offline — the saved experience stays with you" : "Back online — everything is live again"}
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
